import { ApplicationData, At, JobData, RbContext } from './types'
import { message } from './messages'

/**
 * One refused sample per rule, plus the shapes the DBS has actually rejected since go-live (DBS-344).
 *
 * The data is made up: each case carries only the *shape* of a value. The rule tests, the gate-consistency suite
 * and the screenshot generators all read this file, so a rule or message change moves every expectation together.
 * Dates are judged against FIXTURE_TODAY so the fixtures never go stale.
 */

export const FIXTURE_TODAY = '2026-10-30'

/** A complete application every rule accepts, at every point, with the Registered Body side filled in */
export const VALID_APPLICATION: ApplicationData = {
  Title: 'MR', Forename: 'JOHN', PresentSurname: 'SMITH', Gender: 'male', DateOfBirth: '1980-10-13', NiNumber: 'AB123456B',
  CurrentAddress: {
    Address: { AddressLine1: 'MY HOUSE', AddressLine2: 'SOME ROAD', AddressTown: 'TOWNSVILLE', AddressCounty: 'GREATER LONDON', Postcode: 'NW1 0AA', CountryCode: 'GB' },
    ResidentFromGyearMonth: '2010-01',
  },
  PreviousAddress: [{
    Address: { AddressLine1: 'OLD HOUSE', AddressTown: 'TOWNSVILLE', Postcode: 'B6 5JH', CountryCode: 'GB' }, // DBS-357: an A9 9AA postcode with a letter the position rules once refused
    ResidentDates: { ResidentFromGyearMonth: '2000-01', ResidentToGyearMonth: '2010-01' },
  }],
  AdditionalApplicantDetails: {
    BirthSurname: 'JONES', BirthSurnameUntil: '2005', BirthTown: 'LONDON', BirthCounty: 'GREATER LONDON', BirthCountry: 'GB', BirthNationality: 'BRITISH',
    ContactNumber: '07712345678', UnspentConvictions: 'n', DeclarationByApplicant: 'y', LanguagePreference: 'english',
  },
  ApplicantIdentityDetails: {
    IdentityVerified: 'y', EvidenceCheckedBy: 'JANE VERIFIER',
    PassportDetails: { PassportNumber: '123456789', PassportDob: '1980-10-13', PassportNationality: 'BRITISH', PassportIssueDate: '2020-01-01' },
    DriverLicenceDetails: { DriverLicenceNumber: 'SMITH810130J99AA', DriverLicenceDOB: '1980-10-13', DriverLicenceType: 'photo', DriverLicenceValidFrom: '2015-06-01', DriverLicenceIssueCountry: 'GB' },
  },
}

export const VALID_RB: RbContext = { rbNumber: '12345678901', csNumber: '10987654321', addressDetailsChecked: true }

/** The country labels the fixtures need (each repo has its own full list; this is enough for the samples) */
export const FIXTURE_COUNTRY_LABELS: Record<string, string> = {
  GB: 'United Kingdom (UK), including England and Wales, the Channel Islands and Isle of Man; excluding Scotland and Northern Ireland',
  IN: 'India',
  PL: 'Poland',
}

export const fixtureCountryLabelFor = (code: string): string | undefined => FIXTURE_COUNTRY_LABELS[code]

export type ExpectedIssue = { path: string, message: string }

export type RuleFixture = {
  ruleId: string,
  /** What the sample shows */
  title: string,
  /** The DBS rejection history this shape reproduces, e.g. "101 x9 (2026)" */
  history?: string,
  /** Where the issue is first visible */
  at: At,
  /** Applied over VALID_APPLICATION (deep clone) to make the refused sample */
  mutate: (data: ApplicationData) => void,
  /** The issues the rule must produce for the sample, and nothing else */
  expect: ExpectedIssue[],
  complete?: boolean,
  rb?: RbContext,
}

export const cloneValidApplication = (): ApplicationData => JSON.parse(JSON.stringify(VALID_APPLICATION))

const aad = (d: ApplicationData) => d.AdditionalApplicantDetails!
const aid = (d: ApplicationData) => d.ApplicantIdentityDetails!
const previous0 = (d: ApplicationData) => d.PreviousAddress![0]!

export const RULE_FIXTURES: RuleFixture[] = [
  // Helpful checks
  {
    ruleId: 'county.notCountry.uk', title: 'a UK nation typed as the birth county', at: 'birth',
    mutate: d => { aad(d).BirthCounty = 'England' },
    expect: [{ path: 'AdditionalApplicantDetails.BirthCounty', message: message('county.notCountry.uk', 'England') }],
  },
  {
    ruleId: 'county.notCountry.repeat', title: 'the chosen country repeated as the county', at: 'birth',
    mutate: d => { aad(d).BirthCountry = 'IN'; aad(d).BirthCounty = 'INDIA' },
    expect: [{ path: 'AdditionalApplicantDetails.BirthCounty', message: message('county.notCountry.repeat', 'INDIA') }],
  },

  // XSD layer
  {
    ruleId: 'field.constraints', title: 'a character outside the DBS set in a forename', at: 'names',
    mutate: d => { d.Forename = 'JOHN!' },
    expect: [{ path: 'Forename', message: message('field.pattern', 'forename') }],
  },
  {
    ruleId: 'field.constraints', title: 'an address line over the 60-character limit', at: 'addresses',
    mutate: d => { d.CurrentAddress!.Address!.AddressLine1 = 'A'.repeat(61) },
    expect: [{ path: 'CurrentAddress.Address.AddressLine1', message: message('field.tooLong', 'address line 1', 60, 61) }],
  },
  {
    ruleId: 'field.constraints', title: 'a title outside the DBS list', at: 'names',
    mutate: d => { d.Title = 'MX' },
    expect: [{ path: 'Title', message: message('field.enum', 'title') }],
  },
  {
    ruleId: 'field.constraints', title: 'a date of birth that is not a real date', at: 'birth',
    mutate: d => { d.DateOfBirth = '1980-02-30' },
    expect: [{ path: 'DateOfBirth', message: message('field.pattern', 'date of birth') }],
  },
  {
    ruleId: 'field.constraints', title: 'a fourth middle name', at: 'names',
    mutate: d => { d.Middlenames = { Middlename: ['A', 'B', 'C', 'D'] } },
    expect: [{ path: 'Middlenames.Middlename', message: message('field.tooMany', 'middle name', 3) }],
  },
  {
    ruleId: 'field.required', title: 'a finished application without a surname', at: 'server', complete: true,
    mutate: d => { delete d.PresentSurname },
    expect: [{ path: 'PresentSurname', message: message('field.required') }],
  },
  {
    ruleId: 'text.upperCase', title: 'a lower-case forename stored by a direct api call', at: 'server',
    mutate: d => { d.Forename = 'John' },
    expect: [{ path: 'Forename', message: message('text.upperCase') }],
  },

  // Dates
  {
    ruleId: 'date.before1900', title: 'a birth surname "used until" year before 1900', at: 'names',
    mutate: d => { aad(d).BirthSurnameUntil = '1899' },
    expect: [{ path: 'AdditionalApplicantDetails.BirthSurnameUntil', message: message('date.before1900') }],
  },
  {
    ruleId: 'date.future', title: 'a previous address whose "to" month is in the future', history: '103 x1 (a staff correction, 2026)', at: 'addresses',
    mutate: d => { previous0(d).ResidentDates!.ResidentToGyearMonth = '2027-03'; d.CurrentAddress!.ResidentFromGyearMonth = '2027-03' },
    expect: [
      { path: 'CurrentAddress.ResidentFromGyearMonth', message: message('date.future') },
      { path: 'PreviousAddress.0.ResidentDates.ResidentToGyearMonth', message: message('date.future') },
    ],
  },
  {
    ruleId: 'date.beforeBirth', title: 'a current address occupied from before the applicant was born (the family home dated from the birth year)', history: '105 x14 (2025-2026)', at: 'addresses',
    mutate: d => { d.PreviousAddress = []; d.CurrentAddress!.ResidentFromGyearMonth = '1980-01' },
    expect: [{ path: 'CurrentAddress.ResidentFromGyearMonth', message: message('date.beforeBirth') }],
  },
  {
    ruleId: 'date.beforeBirth', title: 'a birth surname "used until" year typed a century out', history: '105 x3 (2026, two typed by staff)', at: 'names',
    mutate: d => { aad(d).BirthSurnameUntil = '1905' },
    expect: [{ path: 'AdditionalApplicantDetails.BirthSurnameUntil', message: message('date.beforeBirth') }],
  },
  {
    ruleId: 'dob.under16', title: 'an applicant under 16', at: 'birth',
    mutate: d => { d.DateOfBirth = '2015-01-01'; aid(d).PassportDetails!.PassportDob = '2015-01-01'; aid(d).DriverLicenceDetails!.DriverLicenceDOB = '2015-01-01' },
    expect: [{ path: 'DateOfBirth', message: message('dob.under16') }],
  },
  {
    ruleId: 'dob.over110', title: 'an applicant over 110', at: 'birth',
    mutate: d => { d.DateOfBirth = '1900-01-01'; aid(d).PassportDetails!.PassportDob = '1900-01-01'; aid(d).DriverLicenceDetails!.DriverLicenceDOB = '1900-01-01' },
    expect: [{ path: 'DateOfBirth', message: message('dob.over110') }],
  },

  // Identity
  {
    ruleId: 'ni.format', title: 'a National Insurance number with a digit where a letter must be', at: 'identity',
    mutate: d => { d.NiNumber = 'A1123456B' },
    expect: [{ path: 'NiNumber', message: message('ni.format') }],
  },
  {
    ruleId: 'ni.prefix', title: 'a National Insurance number with a prefix HMRC never issues', at: 'identity',
    mutate: d => { d.NiNumber = 'BG123456A' },
    expect: [{ path: 'NiNumber', message: message('ni.prefix') }],
  },
  {
    ruleId: 'contact.format', title: 'a contact number that is not a UK number', at: 'identity',
    mutate: d => { aad(d).ContactNumber = '12345' },
    expect: [{ path: 'AdditionalApplicantDetails.ContactNumber', message: message('contact.format') }],
  },
  {
    ruleId: 'language.englishOnly', title: 'Welsh selected as the language', at: 'identity',
    mutate: d => { aad(d).LanguagePreference = 'welsh' },
    expect: [{ path: 'AdditionalApplicantDetails.LanguagePreference', message: message('language.englishOnly') }],
  },
  {
    ruleId: 'title.gender', title: 'Mr with the female gender', at: 'identity',
    mutate: d => { d.Gender = 'female'; aid(d).DriverLicenceDetails!.DriverLicenceNumber = 'SMITH860130J99AA' },
    expect: [
      { path: 'Gender', message: message('title.gender', 'MR', 'female') },
      { path: 'Title', message: message('title.gender', 'MR', 'female') },
    ],
  },

  // Names
  {
    ruleId: 'names.birthSurname.title', title: 'Mrs without a birth surname', at: 'names',
    mutate: d => { d.Title = 'MRS'; d.Gender = 'female'; delete aad(d).BirthSurname; aid(d).DriverLicenceDetails!.DriverLicenceNumber = 'SMITH860130J99AA' },
    expect: [{ path: 'AdditionalApplicantDetails.BirthSurname', message: message('names.birthSurname.title', 'MRS') }],
  },
  {
    ruleId: 'names.birthSurname.female', title: 'a female applicant titled Dr without a birth surname', at: 'names',
    mutate: d => { d.Title = 'DR'; d.Gender = 'female'; delete aad(d).BirthSurname; aid(d).DriverLicenceDetails!.DriverLicenceNumber = 'SMITH860130J99AA' },
    expect: [{ path: 'AdditionalApplicantDetails.BirthSurname', message: message('names.birthSurname.female') }],
  },
  {
    ruleId: 'names.otherName.order', title: 'an other surname used "to" before it was used "from"', at: 'names',
    mutate: d => { aad(d).OtherSurnames = { OtherSurname: [{ Name: 'BROWN', UsedFrom: '2010', UsedTo: '2005' }] } },
    expect: [{ path: 'AdditionalApplicantDetails.OtherSurnames.OtherSurname.0.UsedTo', message: message('names.otherName.order') }],
  },
  {
    ruleId: 'text.placeholder', title: '"N/A" typed into the optional address line 2', at: 'addresses',
    mutate: d => { d.CurrentAddress!.Address!.AddressLine2 = 'N/A' },
    expect: [{ path: 'CurrentAddress.Address.AddressLine2', message: message('text.placeholder', 'N/A') }],
  },

  // Addresses
  {
    ruleId: 'address.postcode.required', title: 'a UK address with no postcode', at: 'addresses',
    mutate: d => { delete d.CurrentAddress!.Address!.Postcode },
    expect: [{ path: 'CurrentAddress.Address.Postcode', message: message('address.postcode.required') }],
  },
  {
    ruleId: 'address.postcode.format', title: 'a UK postcode typed without its space', history: '101 x9 (2026, corrected to GB by staff)', at: 'addresses',
    mutate: d => { d.CurrentAddress!.Address!.Postcode = 'NW10AA' },
    expect: [{ path: 'CurrentAddress.Address.Postcode', message: message('address.postcode.format') }],
  },
  {
    ruleId: 'address.postcode.format', title: 'a postcode that breaks the letter-position rules even with the space', at: 'addresses',
    mutate: d => { previous0(d).Address!.Postcode = 'QW1 0AA' },
    expect: [{ path: 'PreviousAddress.0.Address.Postcode', message: message('address.postcode.format') }],
  },
  {
    ruleId: 'address.previous.order', title: 'a previous address "from" month after its "to" month', history: '106 x1 (2025)', at: 'addresses',
    mutate: d => { previous0(d).ResidentDates = { ResidentFromGyearMonth: '2009-02', ResidentToGyearMonth: '2009-01' }; d.CurrentAddress!.ResidentFromGyearMonth = '2009-01' },
    expect: [{ path: 'PreviousAddress.0.ResidentDates.ResidentFromGyearMonth', message: message('address.previous.order') }],
  },
  {
    ruleId: 'address.gaps', title: 'a gap between the previous and the current address', at: 'addresses',
    mutate: d => { previous0(d).ResidentDates!.ResidentToGyearMonth = '2009-06' },
    expect: [{ path: 'PreviousAddress', message: message('address.gaps') }],
  },
  {
    ruleId: 'address.fiveYears', title: 'an address history under five years', history: '107 x4 (2025-2026, one after staff re-dated every address)', at: 'confirm',
    mutate: d => { d.PreviousAddress = []; d.CurrentAddress!.ResidentFromGyearMonth = '2024-01' },
    expect: [{ path: 'PreviousAddress', message: message('address.fiveYears', 2) }],
  },
  {
    ruleId: 'address.nfa.notGB', title: 'no fixed abode recorded in the UK', at: 'addresses',
    mutate: d => { previous0(d).Address = { AddressLine1: 'NO FIXED ABODE', AddressTown: 'UNKNOWN', CountryCode: 'GB', Postcode: 'NW1 0AB' } },
    expect: [{ path: 'PreviousAddress.0.Address.CountryCode', message: message('address.nfa.notGB') }],
  },

  // Documents
  {
    ruleId: 'documents.licence.number', title: 'a GB driver licence number that does not encode the applicant\'s details', at: 'documents',
    mutate: d => { aid(d).DriverLicenceDetails!.DriverLicenceNumber = 'JONES810130J99AA' },
    expect: [{ path: 'ApplicantIdentityDetails.DriverLicenceDetails.DriverLicenceNumber', message: message('documents.licence.number') }],
  },
  {
    ruleId: 'documents.dob.passport', title: 'a passport date of birth that differs from the applicant\'s (a day typo)', history: '116 x24 (2025-2026)', at: 'confirm',
    mutate: d => { aid(d).PassportDetails!.PassportDob = '1980-10-31' },
    expect: [{ path: 'ApplicantIdentityDetails.PassportDetails.PassportDob', message: message('documents.dob.passport', '1980-10-31', '1980-10-13') }],
  },
  {
    ruleId: 'documents.dob.licence', title: 'a driver licence date of birth with the day and month swapped', history: '116 (2026)', at: 'confirm',
    mutate: d => {
      d.DateOfBirth = '1980-03-07'; aid(d).PassportDetails!.PassportDob = '1980-03-07'
      aid(d).DriverLicenceDetails!.DriverLicenceNumber = 'SMITH803070J99AA'; aid(d).DriverLicenceDetails!.DriverLicenceDOB = '1980-07-03'
    },
    expect: [{ path: 'ApplicantIdentityDetails.DriverLicenceDetails.DriverLicenceDOB', message: message('documents.dob.licence', '1980-07-03', '1980-03-07') }],
  },

  // Declaration
  {
    ruleId: 'declaration.applicant', title: 'the declaration answered no', at: 'confirm',
    mutate: d => { aad(d).DeclarationByApplicant = 'n' },
    expect: [{ path: 'AdditionalApplicantDetails.DeclarationByApplicant', message: message('declaration.applicant') }],
  },
  {
    ruleId: 'declaration.unspentConvictions', title: 'the unspent convictions question unanswered', at: 'confirm',
    mutate: d => { delete aad(d).UnspentConvictions },
    expect: [{ path: 'AdditionalApplicantDetails.UnspentConvictions', message: message('declaration.unspentConvictions') }],
  },

  // Registered Body side
  {
    ruleId: 'identity.verified', title: 'a finished application whose identity was never verified', at: 'server', complete: true,
    mutate: d => { aid(d).IdentityVerified = 'n' },
    expect: [{ path: 'ApplicantIdentityDetails.IdentityVerified', message: message('identity.verified') }],
  },
  {
    ruleId: 'rb.number', title: 'a Registered Body number that is not 11 digits', at: 'server', rb: { ...VALID_RB, rbNumber: '123' },
    mutate: () => undefined,
    expect: [{ path: 'RBdetails.RBNumber', message: message('rb.number') }],
  },
  {
    ruleId: 'rb.csNumber', title: 'a countersignatory number that is not 11 digits', history: '113 x4, 117 x4 (go-live week, pre-live numbers)', at: 'server', rb: { ...VALID_RB, csNumber: 'TEST' },
    mutate: () => undefined,
    expect: [{ path: 'RBdetails.CSigNumber', message: message('rb.csNumber') }],
  },
  {
    ruleId: 'rb.addressChecked', title: 'current address details not checked at verification', at: 'server', rb: { ...VALID_RB, addressDetailsChecked: false },
    mutate: () => undefined,
    expect: [{ path: 'RBdetails.CurrentAddressDetailsChecked', message: message('rb.addressChecked') }],
  },

  // Warnings
  {
    ruleId: 'names.warning', title: 'a title typed into the forename', at: 'confirm',
    mutate: d => { d.Forename = 'MR JOHN' },
    expect: [{ path: 'Forename', message: 'Forename starts with the title "MR". Titles belong in the Title field, not in a name.' }],
  },
]

export type JobFixture = { ruleId: string, title: string, job: JobData, expect: ExpectedIssue[] }

export const VALID_JOB: JobData = { role: 'Care Assistant', application_type: 'enhanced', application_workforce: 'adult_only', working_with_adults: true, working_with_children: false, working_at_home_address: false, volunteer: false }

export const JOB_FIXTURES: JobFixture[] = [
  {
    ruleId: 'job.position', title: 'a role with a character the DBS does not accept', job: { ...VALID_JOB, role: 'Carer: Nights' },
    expect: [{ path: 'role', message: message('job.position', '":" is not accepted by the DBS. Allowed: A-Z 0-9 ( ) - / \' & and spaces') }],
  },
  {
    ruleId: 'job.enhancedRequired.children', title: 'a children\'s barred-list check on a Standard application', job: { ...VALID_JOB, application_type: 'standard', working_with_adults: false, working_with_children: true },
    expect: [{ path: 'application_type', message: message('job.enhancedRequired.children') }, { path: 'working_with_children', message: message('job.enhancedRequired.children') }],
  },
  {
    ruleId: 'job.enhancedRequired.adults', title: 'an adults\' barred-list check on a Standard application', job: { ...VALID_JOB, application_type: 'standard' },
    expect: [{ path: 'application_type', message: message('job.enhancedRequired.adults') }, { path: 'working_with_adults', message: message('job.enhancedRequired.adults') }],
  },
  {
    ruleId: 'job.enhancedRequired.home', title: 'working at the applicant\'s home on a Standard application', job: { ...VALID_JOB, application_type: 'standard', working_with_adults: false, working_at_home_address: true },
    expect: [{ path: 'application_type', message: message('job.enhancedRequired.home') }, { path: 'working_at_home_address', message: message('job.enhancedRequired.home') }],
  },
]
